/**
 * „Hey Vespera“ und „Vespera“ richtig geschrieben. (05.10.2026, Ticket 2928)
 *
 * Martin, 02.10.2026 per Sprechtaste: die Mitschrift schreibt den Namen und
 * das Wach-Wort falsch; er will, dass die Erkennung es künftig richtig liefert.
 *
 * ## Gemessen, bevor gebaut wurde
 *
 * chat_messages, Nutzer-Nachrichten vom 23.09. bis 05.10.2026: 1.600, davon
 * 1.301 über die Sprechtaste. Deren Text kommt aus der Datei-Erkennung
 * (Groq-Whisper): 1.360 Erkennungen im Journal, kein Rückfall auf Soniox, kein
 * Rückfall auf den Live-Text. Die Live-Mitschrift (Soniox) wird nirgends
 * gespeichert und ließ sich nicht messen.
 *
 *   Sprechtaste    das Wach-Wort     31-mal richtig, 35-mal falsch (jedes zweite Mal)
 *                  der Name allein  128-mal richtig, 11-mal falsch
 *   übrige Wege    der Name          45-mal richtig,  1-mal falsch (im Anruf)
 *
 * Die falschen Schreibweisen des Wach-Worts (Anzahl):
 *
 *   in einem Wort     8, 2, 2, 3, 1, 1   sechs Spielarten mit „-a“ am Ende
 *                     1, 1               zwei mit „-er“ am Ende
 *   zerrissen         6                  Schnitt vor dem s
 *                     1, 1               Schnitt nach dem s (Komma, Bindestrich)
 *   Gruß richtig      1                  nur der Name dahinter verhört
 *                     1                  der Name dahinter nach dem s zerrissen
 *
 * Das sind 29 der 35. Der Name allein: vier Schreibweisen (5, 1, 1, 1; eine
 * davon aus dem Anruf) und einmal mit „Hallo“ verschmolzen — 9 der 12. Die
 * Fälle selbst stehen im Test (tests/shared/vespera-schreibweise-2928.test.ts),
 * nicht hier.
 *
 * (Die Bau-Runde zählte 34 und 28: die Nachricht vom 03.10., die nur aus dem
 * Wach-Wort mit zwei Kommas bestand, hatte sie übersehen. Die Prüfung fand
 * sie; seither gibt es die Form GRUSS_UND_ZERRISSEN.)
 *
 * ## Warum eine eigene, enge Regel
 *
 * Die Sprechtaste läuft nicht durch `richteTranskriptZu`; dort greift von der
 * ganzen Kette nur die feste Namensregel (server/eigene-begriffe.ts,
 * `korrigiereFesteNamen`). Die Kölner Phonetik (tools/vocabulary.ts) fände nur
 * die Schreibweisen mit gleichem Klang und gleicher Länge; die Abstandsregel
 * (shared/begriffe.ts) beginnt bei acht Buchstaben. Ein verschmolzenes oder
 * zerrissenes Wach-Wort erreicht keine von beiden.
 *
 * Darum steht hier das Lautgerüst des Wach-Worts mit genau den Vertauschungen,
 * die gemessen wurden — eine endliche Menge von Schreibweisen, kein Abstand,
 * kein Raten:
 *
 *   H E (Y)   V E S   P E R A
 *             │ │ │   │ │ │ └─ a · er      („-er“ klingt im Deutschen wie „-a“)
 *             │ │ │   │ │ └─── r · rr
 *             │ │ │   │ └───── e · a · ai
 *             │ │ │   └─────── p · b · v
 *             │ │ └─────────── s · ss
 *             │ └───────────── e · i
 *             └─────────────── v · w · b
 *
 * ⚠️ „Vesper“ (Abendgebet, Jause), „Wespe“, „Vespa“ und „Vera“ sind Wörter und
 *    bleiben. Deshalb verlangt jede Form das ganze Gerüst bis zur Endung, und
 *    die zerrissenen Formen verlangen den Gruß OHNE Lücke vor der ersten Silbe:
 *    „Hey, bis Vera kommt“ trägt dieselben Laute und ist ein Satz. Steht der
 *    Gruß für sich, muss vor UND hinter der ersten Silbe ein Komma oder ein
 *    Bindestrich stehen („Hey, bis, Bera!“) — das schreibt niemand in einem Satz.
 * ⚠️ Der Name allein wird nur in den gemessenen Schreibweisen ersetzt und nur
 *    groß geschrieben: klein mitten im Satz ist „víspera“ Spanisch (der
 *    Vorabend), und „Wisperer“ ist ein Wort.
 * ⚠️ Die Schreibweise auf „-erer“ ist die häufigste des Namens (5 der 12) und
 *    die einzige, die auch ein Wort sein KÖNNTE (wer vespert). Im ganzen
 *    Bestand — 11.444 Nutzer-Nachrichten — meinte sie achtmal von achtmal den
 *    Namen. Sagt jemand das Wort wirklich, steht danach fälschlich der Name:
 *    das ist der bewusst getragene Rest dieser Regel.
 * ⚠️ Steht der Name schon richtig da, bleibt die ganze Stelle, wie sie ist —
 *    auch „Hey, Vespera“ mit Komma und ein klein getipptes „hey vespera“.
 * ⚠️ Für alle Konten: der Name ist der des Produkts, und keine der
 *    Schreibweisen ist ein deutsches Wort. (Anders als „Jeff“, das ein Vorname
 *    ist — shared/jev-schreibweise.ts gilt nur für Konto 1.)
 *
 * ## Was offen bleibt
 *
 * Sechs einmalige Schreibweisen des Wach-Worts und drei des Namens liegen
 * außerhalb des Gerüsts (ein fremdes Wort davor, ein Laut zu viel, der Anfang
 * verschluckt). Sie bleiben stehen: eine Lücke statt eines geratenen Namens.
 */

/** Der Name, wie er geschrieben wird. */
const NAME = "Vespera";

/*
 * Wortanfang und Wortende wie bei „Jev“ (shared/jev-schreibweise.ts): kein
 * Buchstabe und keine Ziffer daneben, und nichts, was auf eine Adresse oder
 * einen Dateinamen deutet (voice.vespera.at, …@…, …/…).
 */
const VORN = String.raw`(?<![\p{L}\p{N}/@._:])`;
const HINTEN = String.raw`(?![\p{L}\p{N}@/])`;

/** „Ves-per“ in den gemessenen Spielarten — ohne die Endung. */
const KERN = String.raw`[vwb][ei]ss?[pbv](?:e|a|ai)rr?`;

/** Was die Erkennung zwischen zwei Teile setzt: Leerzeichen, Komma davor, Bindestrich. */
const LUECKE = String.raw`(?:-|,?[ \u00a0])`;

/** Wie LUECKE, aber nie ein bloßes Leerzeichen: Bindestrich oder Komma mit Leerzeichen. */
const TRENNER = String.raw`(?:-|,[ \u00a0])`;

/** Gruß und Name in EINEM Wort; Endung „-a“ oder „-er“. */
const VERSCHMOLZEN = new RegExp(`${VORN}(he[yi]?|hallo)${KERN}(?:a|er)${HINTEN}`, "giu");

/**
 * Das Wach-Wort in zwei Teilen: der Schnitt vor dem s oder nach dem s. Der
 * Gruß hängt ohne Lücke an der ersten Silbe — das trennt es von einem Satz.
 */
const ZERRISSEN = new RegExp(
  `${VORN}he[vwb][ei](?:${LUECKE}s|ss?${LUECKE})[pbv](?:e|a|ai)rr?a${HINTEN}`,
  "giu",
);

/** Der Gruß steht richtig, nur der Name dahinter ist verhört. */
const NACH_GRUSS = new RegExp(`${VORN}(he[yi]${LUECKE})(${KERN}a)${HINTEN}`, "giu");

/**
 * Der Gruß steht richtig und für sich, der Name dahinter ist nach dem s
 * zerrissen: „Hey, bis, Bera!“ (03.10.2026, die ganze Nachricht).
 *
 * ⚠️ Auf BEIDEN Seiten der ersten Silbe ist ein Komma oder ein Bindestrich
 *    Pflicht, ein bloßes Leerzeichen reicht nicht: „Hey, bis Vera kommt,
 *    warten wir.“ ist ein Satz und bleibt. Im ganzen Verlauf (Nachrichten
 *    und Antworten) steht ein Komma zwischen diesen beiden Silben genau
 *    zweimal — in dieser Nachricht und in „Heves, Bera“ (ZERRISSEN).
 */
const GRUSS_UND_ZERRISSEN = new RegExp(
  `${VORN}(he[yi]${TRENNER})([vwb][ei]ss?${TRENNER}[pbv](?:e|a|ai)rr?a)${HINTEN}`,
  "giu",
);

/**
 * Der Name allein: die vier gemessenen Schreibweisen, groß geschrieben, auch
 * im Genitiv. Bewusst eine Liste und kein Gerüst — siehe Kopf.
 */
const ALLEIN = new RegExp(`${VORN}(?:Wespera|Vispera|Vespiera|Vesperer)(['’]?s)?${HINTEN}`, "gu");

/**
 * Verhörte Schreibweisen von „Hey Vespera“ und „Vespera“ richtigstellen.
 *
 * Idempotent: Was richtig steht, bleibt unangetastet.
 *
 * @param merke  wird je Ersetzung mit (vorher, nachher) gerufen — für die
 *               Zählzeile im Protokoll; nur echte Änderungen.
 */
export function korrigiereVespera(text: string, merke?: (vorher: string, nachher: string) => void): string {
  if (!text) return text;
  const melde = (vorher: string, nachher: string): string => {
    if (vorher !== nachher) merke?.(vorher, nachher);
    return nachher;
  };
  return text
    .replace(VERSCHMOLZEN, (treffer: string, gruss: string) =>
      melde(treffer, `${gruss.toLowerCase() === "hallo" ? "Hallo" : "Hey"} ${NAME}`),
    )
    .replace(ZERRISSEN, (treffer: string) => melde(treffer, `Hey ${NAME}`))
    .replace(NACH_GRUSS, (treffer: string, gruss: string, name: string) => {
      // Der Name steht schon richtig: nichts anfassen, auch den Gruß nicht.
      if (name.toLowerCase() === NAME.toLowerCase()) return treffer;
      melde(name, NAME);
      return `${gruss}${NAME}`;
    })
    .replace(GRUSS_UND_ZERRISSEN, (_treffer: string, gruss: string, name: string) => `${gruss}${melde(name, NAME)}`)
    .replace(ALLEIN, (treffer: string, genitiv: string | undefined) => melde(treffer, genitiv ? `${NAME}s` : NAME));
}
